import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Trash2 } from "lucide-react";

import { Avatar } from "../../components/Avatar/Avatar";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { useOptionalToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";
import { deleteMessage, listMessages, postMessage } from "./api";
import type { ChatMessage } from "./types";
import styles from "./GuildChatFeed.module.css";

interface GuildChatFeedProps {
  projectId: string;
  guildId: string;
  /** Page size for history requests. Defaults to 25. */
  pageSize?: number;
}

// === Formatting ====================================================== //

const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "Mar 2 · 18:04" — message timestamp, mirrors the mockup `.msg-time`. */
function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  // Intl yields "Mar 2, 18:04"; the mockup uses a middot separator.
  const parts = TIME_FMT.formatToParts(d);
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  return `${month} ${day} · ${hour}:${minute}`;
}

/** Reverse a server page (newest-first) into oldest→newest order. */
function oldestFirst(items: ChatMessage[]): ChatMessage[] {
  return [...items].reverse();
}

/**
 * The Chat section of `GuildDetailPage` — ports the Chat card of
 * docs/mockups/guild-detail.html (screen 1 feed + compose, screen 4 delete
 * confirm + empty state).
 *
 * The feed renders oldest→newest with the newest message at the bottom. The
 * server returns each page newest-first, so each page is reversed and pages are
 * accumulated: "Load older" fetches the next (older) page and prepends it,
 * posting appends to the bottom, deleting removes a single row.
 */
export function GuildChatFeed({
  projectId,
  guildId,
  pageSize = 25,
}: GuildChatFeedProps) {
  const toast = useOptionalToast();
  const qc = useQueryClient();

  // Accumulated pages, oldest page first. Each page is already oldest→newest.
  const [pages, setPages] = useState<ChatMessage[][]>([]);
  // The page index to request on the next "Load older" click.
  const [nextPage, setNextPage] = useState(2);
  const [total, setTotal] = useState(0);
  const [seeded, setSeeded] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [composeBody, setComposeBody] = useState("");
  const [composeError, setComposeError] = useState<string | null>(null);

  // The message queued for the delete-confirm modal.
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // --- Initial load (page 1) ----------------------------------------- //
  const initial = useQuery({
    queryKey: ["guild-messages", projectId, guildId, 1],
    queryFn: () =>
      listMessages(projectId, guildId, { page: 1, limit: pageSize }),
  });

  // Seed local state once from the page-1 query result.
  useEffect(() => {
    if (seeded || !initial.data) return;
    setPages([oldestFirst(initial.data.items)]);
    setTotal(initial.data.total);
    setSeeded(true);
  }, [seeded, initial.data]);

  const messages = pages.flat();
  const loadedCount = messages.length;
  const hasOlder = seeded && loadedCount < total;

  // --- Load older ----------------------------------------------------- //
  async function handleLoadOlder() {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await listMessages(projectId, guildId, {
        page: nextPage,
        limit: pageSize,
      });
      // Prepend the older page as a new first entry — feed stays oldest→newest.
      setPages((prev) => [oldestFirst(res.items), ...prev]);
      setNextPage((p) => p + 1);
      // `total` is a mixed local/server value: local posts/deletes adjust it
      // optimistically, so a stale server count must never drag it backward
      // (that would make `loadedCount` exceed `total` and hide the feed).
      setTotal((t) => Math.max(t, res.total));
    } catch (err) {
      toast?.push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setLoadingOlder(false);
    }
  }

  // --- Compose / post ------------------------------------------------- //
  const postMutation = useMutation({
    mutationFn: (body: string) => postMessage(projectId, guildId, body),
    onSuccess: (msg) => {
      // Append to the last (newest) page so the feed grows at the bottom.
      setPages((prev) => {
        if (prev.length === 0) return [[msg]];
        const next = prev.slice();
        next[next.length - 1] = [...next[next.length - 1], msg];
        return next;
      });
      setTotal((t) => t + 1);
      setComposeBody("");
      setComposeError(null);
      void qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: (err) => {
      setComposeError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function handleSend() {
    const body = composeBody.trim();
    if (!body || postMutation.isPending) return;
    postMutation.mutate(body);
  }

  // --- Delete --------------------------------------------------------- //
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) =>
      deleteMessage(projectId, guildId, messageId),
    onSuccess: (_void, messageId) => {
      setPages((prev) =>
        prev.map((page) => page.filter((m) => m.id !== messageId)),
      );
      setTotal((t) => Math.max(0, t - 1));
      setPendingDelete(null);
      setDeleteError(null);
      void qc.invalidateQueries({
        queryKey: ["project", projectId, "activity"],
      });
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function openDelete(msg: ChatMessage) {
    setDeleteError(null);
    setPendingDelete(msg);
  }

  function closeDelete() {
    if (deleteMutation.isPending) return;
    setDeleteError(null);
    setPendingDelete(null);
  }

  const loading = initial.isLoading && !seeded;
  const hasError = initial.isError && !seeded;
  const isEmpty = seeded && total === 0;
  const initialErrorMessage =
    initial.error instanceof ApiError
      ? describeError(initial.error.code)
      : describeError("internal");

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Chat</h3>
        <span className={styles.cardSub}>guild chat · newest at the bottom</span>
      </div>

      {hasOlder && (
        <div className={styles.chatOlder}>
          <button
            type="button"
            className={styles.link}
            onClick={() => void handleLoadOlder()}
            disabled={loadingOlder}
          >
            ↑ Load older messages
          </button>
        </div>
      )}

      <div className={styles.chatFeed} data-testid="chat-feed">
        {loading && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {hasError && (
          <div className={styles.empty} role="alert">
            {initialErrorMessage}
          </div>
        )}

        {isEmpty && (
          <div className={styles.empty}>No messages yet.</div>
        )}

        {!loading &&
          !hasError &&
          !isEmpty &&
          messages.map((msg) =>
            msg.kind === "system" ? (
              <SystemMessage
                key={msg.id}
                msg={msg}
                onDelete={() => openDelete(msg)}
              />
            ) : (
              <PlayerMessage
                key={msg.id}
                msg={msg}
                onDelete={() => openDelete(msg)}
              />
            ),
          )}
      </div>

      <div className={styles.chatCompose}>
        <div className={styles.composeRow}>
          <textarea
            className={styles.composeInput}
            placeholder="Message the guild as an admin…"
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            disabled={postMutation.isPending}
          />
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={composeBody.trim().length === 0 || postMutation.isPending}
            loading={postMutation.isPending}
            icon={<Send size={13} aria-hidden="true" />}
          >
            Send
          </Button>
        </div>
        {composeError && (
          <p className={styles.composeError} role="alert">
            {composeError}
          </p>
        )}
        <div className={styles.helper}>
          Posts a <code className={styles.code}>system</code> message — visible
          in-game to every guild member.
        </div>
      </div>

      {pendingDelete && (
        <DeleteMessageModal
          msg={pendingDelete}
          error={deleteError}
          busy={deleteMutation.isPending}
          onClose={closeDelete}
          onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
        />
      )}
    </div>
  );
}

// === Message rows ==================================================== //

/** A player chat message — avatar + name + time + body. Mockup `.msg`. */
function PlayerMessage({
  msg,
  onDelete,
}: {
  msg: ChatMessage;
  onDelete: () => void;
}) {
  const name = msg.sender_name ?? "?";
  return (
    <div className={styles.msg}>
      <span className={styles.avSm}>
        <Avatar name={name} size={30} />
      </span>
      <div className={styles.msgMain}>
        <div className={styles.msgHead}>
          <span className={styles.msgName}>{name}</span>
          <span className={styles.msgTime}>
            {formatMessageTime(msg.created_at)}
          </span>
        </div>
        <div className={styles.msgBody} data-testid="chat-msg-body">
          {msg.body}
        </div>
      </div>
      <button
        type="button"
        className={styles.msgDel}
        aria-label="Delete message"
        title="Delete message"
        onClick={onDelete}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </div>
  );
}

/** A system message — centered pill with a System tag. Mockup `.sys-msg`. */
function SystemMessage({
  msg,
  onDelete,
}: {
  msg: ChatMessage;
  onDelete: () => void;
}) {
  return (
    <div className={styles.sysMsg}>
      <span className={styles.sysInner}>
        <span className={styles.sysTag}>System</span>
        <span data-testid="chat-msg-body">{msg.body}</span>
      </span>
      <button
        type="button"
        className={styles.msgDel}
        aria-label="Delete message"
        title="Delete message"
        onClick={onDelete}
      >
        <Trash2 size={13} aria-hidden="true" />
      </button>
    </div>
  );
}

// === Delete-confirm modal ============================================ //

/** Delete-message confirm modal — mockup screen 4. */
function DeleteMessageModal({
  msg,
  error,
  busy,
  onClose,
  onConfirm,
}: {
  msg: ChatMessage;
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const author = msg.sender_name ?? "System";
  return (
    <Modal
      open
      onClose={onClose}
      title="Delete message?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            icon={<Trash2 size={14} aria-hidden="true" />}
          >
            Delete
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        Permanently removes this message from the guild chat —{" "}
        <b>hard delete, no undo</b>.
      </p>
      <div className={styles.modalQuote}>
        {author} — “{msg.body}”
      </div>
      {error && (
        <p className={`${styles.modalBody} ${styles.modalError}`} role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

// === Loading skeleton ================================================ //

function SkeletonRow() {
  return (
    <div className={styles.msg} aria-hidden="true">
      <span className={`${styles.avSm} ${styles.skeletonAv}`} />
      <div className={styles.msgMain}>
        <div className={styles.skeletonLineShort} />
        <div className={styles.skeletonLine} />
      </div>
    </div>
  );
}
